/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { InfoBlock, RadioButton, RangeMonthPicker } from '../../../components/common';
import { FlexStyled } from '../../../components/common/Block/Flexbox';
import { LayoutStyled } from '../../../components/common/Block/Layout';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { EColorStatus } from '../../../modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardExpensesDetail,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { IWizardExpensesDetailTranslation } from '../../../modules/internationalization/types/wizards/expensesDetail';
import { IRootReducer } from '../../../reducers/rootReducer';
import { formatDateToMMYYYY } from '../../../utils/formatText';
import { TWizardExpensesDetailUpdateDataPayload } from '../types/wizardExpensesDetailActionTypes';
import { IWizardExpensesDetailHierarchyLevel } from '../types/wizardExpensesDetailTypes';
import { wizardExpensesDetailUpdateDataAction } from '../wizardExpensesDetailActions';
import { IWizardExpensesDetailReducer } from '../wizardExpensesDetailReducer';
import {
    makeSelectWizardExpensesDetailDate,
    makeSelectWizardExpensesDetailFormatsAvailable,
    makeSelectWizardExpensesDetailFormatsAvailableChosen,
    makeSelectWizardExpensesDetailHierarchyLevels,
    makeSelectWizardExpensesDetailHierarchyLevelsChosen,
    makeSelectWizardExpensesDetailMaxDate,
} from '../wizardExpensesDetailSelectors';
import { WizardExpensesDetailStyled } from './styled/WizardExpensesDetailStyled';

const WizardExpensesDetailSelectParams = (props: IConnectedProps & IDispatchProps) => {
    const {
        date,
        formatsAvailable,
        formatsAvailableChosen,
        hierarchyLevels,
        hierarchyLevelsChosen,
        maxDate,
        translationCommon,
        wizardExpensesDetailTranslation,

        updateData,
    } = props;

    const onLevelChangeHandler = useCallback(
        (level: IWizardExpensesDetailHierarchyLevel) => {
            if (level.id === hierarchyLevelsChosen.id) return;

            updateData({ hierarchyLevelsChosen: level });
        },
        [hierarchyLevelsChosen, updateData],
    );

    const onFormatChangeHandler = useCallback(
        (format: string) => {
            if (format === formatsAvailableChosen) return;

            updateData({ formatsAvailableChosen: format });
        },
        [formatsAvailableChosen, updateData],
    );

    const onDateChangeHandler = useCallback(
        (newDate: Moment) => {
            updateData({ date: newDate.toISOString() });
        },
        [updateData],
    );

    const getRangeLabel = useCallback(
        () => formatDateToMMYYYY(date ? moment(date) : moment(maxDate), translationCommon.calendar.months),
        [date, maxDate, translationCommon],
    );

    return (
        <WizardExpensesDetailStyled>
            <div>
                {hierarchyLevels.length > 1 && (
                    <div className="wizard-expenses-detail__block">
                        <div className="wizard-expenses-detail__title">
                            {wizardExpensesDetailTranslation.hierarchyLevels}
                        </div>
                        {hierarchyLevels.map((level) => (
                            <ArrowTooltip
                                key={level.id}
                                placement="right-end"
                                title={!level.isAvailable ? level.reason : ''}
                            >
                                <LayoutStyled
                                    className={classNames('mb-3 wizard-expenses-detail__hierarchy', {
                                        'wizard-expenses-detail__hierarchy_disabled': !level.isAvailable,
                                    })}
                                    data-name={`expenses-detail-hierarchy-${level.id}`}
                                >
                                    <RadioButton
                                        checked={level.id === hierarchyLevelsChosen.id}
                                        dataName={`expenses-detail-hierarchy-radio-${level.id}`}
                                        disabled={!level.isAvailable}
                                        label={level.label}
                                        onChange={() => onLevelChangeHandler(level)}
                                    />
                                </LayoutStyled>
                            </ArrowTooltip>
                        ))}
                    </div>
                )}

                <div className="wizard-expenses-detail__block wizard-expenses-detail__block_width">
                    <div className="wizard-expenses-detail__title">{wizardExpensesDetailTranslation.period}</div>
                    {maxDate && (
                        <RangeMonthPicker
                            isSingle
                            label={getRangeLabel()}
                            maxDate={moment(maxDate)}
                            rangeEnd={date ? moment(date) : moment(maxDate)}
                            rangeStart={date ? moment(date) : moment(maxDate)}
                            onSelect={onDateChangeHandler}
                        />
                    )}
                </div>

                <div className="wizard-expenses-detail__block">
                    <div className="wizard-expenses-detail__title">
                        {wizardExpensesDetailTranslation.formatsAvailable}
                    </div>
                    <FlexStyled>
                        {formatsAvailable.map((format) => (
                            <LayoutStyled className="mb-3 mr-3" key={format}>
                                <RadioButton
                                    checked={format === formatsAvailableChosen}
                                    dataName={`expenses-detail-format-radio-${format}`}
                                    label={format}
                                    onChange={() => onFormatChangeHandler(format)}
                                />
                            </LayoutStyled>
                        ))}
                    </FlexStyled>
                </div>
            </div>

            <div className="wizard-expenses-detail__block">
                <InfoBlock colorStatus={EColorStatus.WARNING}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: wizardExpensesDetailTranslation.info,
                        }}
                    />
                </InfoBlock>
            </div>
        </WizardExpensesDetailStyled>
    );
};

export interface IConnectedProps
    extends Pick<
        IWizardExpensesDetailReducer,
        'date' | 'formatsAvailable' | 'formatsAvailableChosen' | 'hierarchyLevels' | 'hierarchyLevelsChosen' | 'maxDate'
    > {
    translationCommon: ICommonTranslation;
    wizardExpensesDetailTranslation: IWizardExpensesDetailTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    date: makeSelectWizardExpensesDetailDate(),
    formatsAvailable: makeSelectWizardExpensesDetailFormatsAvailable(),
    formatsAvailableChosen: makeSelectWizardExpensesDetailFormatsAvailableChosen(),
    hierarchyLevels: makeSelectWizardExpensesDetailHierarchyLevels(),
    hierarchyLevelsChosen: makeSelectWizardExpensesDetailHierarchyLevelsChosen(),
    maxDate: makeSelectWizardExpensesDetailMaxDate(),
    translationCommon: makeSelectTranslationCommon(),
    wizardExpensesDetailTranslation: makeSelectTranslationWizardExpensesDetail(),
});

export interface IDispatchProps {
    updateData: (payload: TWizardExpensesDetailUpdateDataPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    updateData: (payload) => dispatch(wizardExpensesDetailUpdateDataAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardExpensesDetailSelectParams);
